import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import Dialog from '@src/components/dialog'
import classes from '@src/styles/vs-competition-quote-modal.module.css'
import { segmentTrack } from '@src/functions/segment'
import useCalendlyMessages from '@src/hooks/useCalendlyMessages'
import { buildCalendlyUrl, eventIdFromUri } from '@src/functions/calendly'
import {
  notifyCalendlyScheduled,
  submitVsCompetitionLead,
  type VsCompetitionLeadResult,
} from '@src/functions/vs-competition-lead'

export const QUOTE_MODAL_COPY = {
  formHeading: 'New customer inquiry',
  formSub:
    "Tell us what you need handled and pick a time with our team — we'll come to the " +
    'call with pricing.',
  submit: 'Schedule a meeting',
  calendlyHeading: 'Schedule a meeting',
  calendlySub: 'Pick a time with your account manager. Your request has been submitted.',
}

/** 620px is the height the booking app's own Calendly step uses. */
const CALENDLY_HEIGHT = 620

const EMPTY = {
  fullName: '',
  companyName: '',
  phone_number: '',
  email: '',
  description: '',
}

type Values = typeof EMPTY

type FieldProps = {
  label: string
  name: keyof Values
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  type?: string
  placeholder?: string
  multiline?: boolean
}

function Field({ label, name, value, onChange, type, placeholder, multiline }: FieldProps) {
  const id = `vs-quote-${name}`
  const shared = {
    id,
    name,
    value,
    onChange,
    placeholder,
    required: true,
    className: classes.input,
  }

  return (
    <div className={classes.field}>
      <label className={classes.label} htmlFor={id}>
        {label} <span className={classes.required}>*</span>
      </label>
      {multiline ? <textarea rows={3} {...shared} /> : <input type={type ?? 'text'} {...shared} />}
    </div>
  )
}

export type QuoteModalProps = {
  open: boolean
  onClose: () => void
}

/**
 * Two steps in one dialog: the contact form, then the Calendly widget that replaces it
 * once the lead is in — the same beat as the booking app's solar buyer modal, which
 * swaps its own contents rather than opening a second dialog.
 *
 * The step only ever moves forward on a resolved submit, but that submit resolves even
 * when the backend call failed (see submitVsCompetitionLead) — a visitor who reaches
 * this point should always get a calendar.
 */
export default function QuoteModal({ open, onClose }: QuoteModalProps) {
  const [values, setValues] = useState<Values>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [lead, setLead] = useState<VsCompetitionLeadResult | null>(null)

  // A visitor who books, closes and reopens should get a fresh form rather than the
  // calendar they already used.
  useEffect(() => {
    if (!open) return
    setValues(EMPTY)
    setLead(null)
    setSubmitting(false)
  }, [open])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target
    setValues((previous) => ({ ...previous, [name]: value }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)

    segmentTrack('Vs Competition - Quote Form Submitted', {
      page: 'Vs Competition',
      companyName: values.companyName,
      email: values.email,
    })

    const result = await submitVsCompetitionLead(values)

    setLead(result)
    setSubmitting(false)

    segmentTrack('Vs Competition - Calendly Displayed', {
      page: 'Vs Competition',
      agent: result.agent,
      leadRecorded: result.success,
    })
  }

  // Keyed on the values rather than the object so that the iframe src is stable across
  // renders — a new src reloads the widget underneath whoever is mid-booking.
  const src = useMemo(
    () =>
      lead
        ? buildCalendlyUrl(lead.link, {
            name: values.companyName
              ? `${values.fullName} at ${values.companyName}`
              : values.fullName,
            email: values.email,
            smsReminderNumber: values.phone_number,
            customAnswers: { a1: values.description },
          })
        : null,
    [
      lead,
      values.fullName,
      values.companyName,
      values.email,
      values.phone_number,
      values.description,
    ],
  )

  useCalendlyMessages((event, payload) => {
    if (!lead || event !== 'calendly.event_scheduled') return

    const eventUri: string | null = payload?.event?.uri ?? null
    const eventId = eventIdFromUri(eventUri)

    segmentTrack('Vs Competition - Calendly Scheduled', {
      page: 'Vs Competition',
      agent: lead.agent,
      eventId,
    })

    notifyCalendlyScheduled({
      formDraftId: lead.formDraftId,
      eventId,
      agent: lead.agent,
    })
  })

  return (
    <Dialog show={open} onHide={onClose}>
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='vs-quote-heading'
        className={classNames(classes.container, { [classes.containerWide]: Boolean(lead) })}
      >
        <button type='button' className={classes.close} aria-label='Close' onClick={onClose}>
          ×
        </button>

        <h2 id='vs-quote-heading' className={classes.heading}>
          {lead ? QUOTE_MODAL_COPY.calendlyHeading : QUOTE_MODAL_COPY.formHeading}
        </h2>
        <p className={classes.sub}>
          {lead ? QUOTE_MODAL_COPY.calendlySub : QUOTE_MODAL_COPY.formSub}
        </p>

        {lead ? (
          <div className={classes.embed}>
            {src ? (
              <iframe
                title='Schedule a meeting'
                src={src}
                className={classes.frame}
                height={CALENDLY_HEIGHT}
                allow='camera; microphone; fullscreen'
              />
            ) : (
              <p className={classes.loading}>Loading available times…</p>
            )}
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Field
              label='Full Name'
              name='fullName'
              value={values.fullName}
              onChange={handleChange}
              placeholder='ex. John Smith'
            />
            <Field
              label='Company Name'
              name='companyName'
              value={values.companyName}
              onChange={handleChange}
              placeholder='ex. Dimension'
            />
            <Field
              label='Phone Number'
              name='phone_number'
              type='tel'
              value={values.phone_number}
              onChange={handleChange}
              placeholder='ex. 123-456-7890'
            />
            <Field
              label='Email'
              name='email'
              type='email'
              value={values.email}
              onChange={handleChange}
              placeholder='ex. hello@yourcompany.com'
            />
            <Field
              label='Briefly describe your hazardous waste needs'
              name='description'
              value={values.description}
              onChange={handleChange}
              placeholder='Describe volume, frequency and pickup address'
              multiline
            />

            <button type='submit' className={classes.submit} disabled={submitting}>
              {submitting ? 'Submitting…' : QUOTE_MODAL_COPY.submit}
            </button>
          </form>
        )}
      </div>
    </Dialog>
  )
}
